<template>
  <g class="top-side-view">
    <clipPath :id="'clip-path-' + _uid">
      <rect :width="width" :height="height"></rect>
    </clipPath>
    <user-space
      v-slot="{ xRange, yRange }"
      :top="(width / 16) * 9 - height"
      :width="width"
      :height="width"
      :layout="layout"
      :clip-path="`url(#clip-path-${_uid})`"
    >
      <tile-plot
        :get-tile-url="getTileUrl"
        :get-tile-index="getTileIndex"
        :get-tile-position="getTilePosition"
        :x-scale="getScale(domain.x, xRange)"
        :y-scale="getScale(domain.y, yRange)"
      >
      </tile-plot>
      <top-view-plot
        :features="filteredData"
        :classed="getClass"
        :bearing="bearing"
        :x-scale="getScale(domain.x, xRange)"
        :y-scale="getScale(domain.y, yRange)"
      >
      </top-view-plot>
    </user-space>

    <path
      :class="['vg-label-bg', colorClass]"
      d="M600,25 h60 a10,10 0 0 1 10,10 v10 a10,10 0 0 1 -10,10 h-60 a10,10 0 0 1 -10,-10 v-10 a10,10 0 0 1 10,-10 z"
    />
    <text-label :x="width - 90" :y="40" anchor="middle">{{
      year || ""
    }}</text-label>

    <user-space
      v-slot="{ xRange, yRange }"
      :top="(width / 16) * 9"
      :width="width"
      :height="height - (width / 16) * 9"
      :layout="layout"
      :clip-path="`url(#clip-path-${_uid})`"
    >
      <side-view-plot
        :features="filteredData"
        :classed="getClass"
        :bearing="bearing"
        :x-scale="getScale(domain.x, xRange)"
        :y-scale="getScale(domain.h, yRange)"
        :height="h"
      >
      </side-view-plot>
    </user-space>
  </g>
</template>

<script>
import TilePlot, {
  defaultGetTileIndex,
  defaultGetTilePosition
} from 'vue-graphics/components/TilePlot.vue'
import TopViewPlot from 'vue-graphics/components/TopViewPlot.vue'
import SideViewPlot from 'vue-graphics/components/SideViewPlot.vue'
import UserSpace from 'vue-graphics/hocs/UserSpace'
import TextLabel from 'vue-graphics/elements/TextLabel'
import {DomainHelper, TransformHelper} from 'vue-graphics/helpers'
import {scaleLinear} from 'd3'

export default {
  name: 'TopSideView',
  components: {TilePlot, TopViewPlot, SideViewPlot, UserSpace, TextLabel},
  inheritAttrs: false,
  props: {
    width: {
      type: Number,
      required: true
    },
    height: {
      type: Number,
      required: true
    },
    data: {
      type: Array,
      required: true
    },
    h: {
      type: Function,
      required: true
    },
    bbox: {
      type: Array,
      default: () => [0, 0, 0, 1, 1, 1]
    },
    year: {
      type: Number,
      required: true
    },
    bearing: {
      type: Number,
      default: 0
    }
    // color: {
    //   type: String,
    //   default: 'y1960'
    // }
  },
  computed: {
    colorClass () {
      const {year} = this

      if (year < 1970) return 'y1960'
      if (year < 1980) return 'y1970'
      if (year < 1990) return 'y1980'
      if (year < 2000) return 'y1990'
      if (year < 2010) return 'y2000'
      if (year < 2020) return 'y2010'
      if (year < 2030) return 'y2020'

      return ''
    },
    layout () {
      return new TransformHelper().flipY()
    },
    filteredData () {
      const {year} = this
      return this.data.filter((f) => f.properties.built <= year)
    },
    domainStatic () {
      const {data, h, bbox} = this
      const xDomain = DomainHelper.BBOX(0)(data)
      const yDomain = DomainHelper.BBOX(1)(data)
      const hDomain = DomainHelper.CLAMPED_MINMAX(
        0,
        0
      )(data.map((f) => [h(f)]))
      const xt = 0.5 * (bbox[0] + bbox[3])
      const yt = 0.5 * (bbox[1] + bbox[4])
      const xMid = (1 - xt) * xDomain[0] + xt * xDomain[1]
      const yMid = (1 - yt) * yDomain[0] + yt * yDomain[1]
      const halfwidth =
        0.5 *
        Math.max(
          (xDomain[1] - xDomain[0]) * (bbox[3] - bbox[0]),
          (yDomain[1] - yDomain[0]) * (bbox[4] - bbox[1])
        )
      const hMin = (1 - bbox[2]) * hDomain[0] + bbox[2] * hDomain[1]
      const hMax = (1 - bbox[5]) * hDomain[0] + bbox[5] * hDomain[1]
      return [xMid, yMid, halfwidth, hMin, hMax]
    },
    domain () {
      const [xMid, yMid, halfwidth, hMin, hMax] = this.domainStatic
      const [xCenter, yCenter] = new TransformHelper()
        .rotate(this.bearing)
        .apply([xMid, yMid])
      return {
        x: [xCenter - halfwidth, xCenter + halfwidth],
        y: [yCenter - halfwidth, yCenter + halfwidth],
        h: [hMin, hMax]
      }
    },
    getTileUrl () {
      return 'https://api.mapbox.com/styles/v1/chachopazos/ckwym3j7z0f3414o0jq50dgli/tiles/256/{z}/{x}/{y}@2x?access_token=pk.eyJ1IjoiY2hhY2hvcGF6b3MiLCJhIjoiY2pkMDN3eW4wNHkwZDJ5bGc0cnpueGNxbCJ9.WWWg_OnK5e7L1RknMliY4A'
    }
  },
  methods: {
    getScale (domain, range) {
      return scaleLinear().domain(domain).range(range)
    },
    getClass (f) {
      const built = f.properties.built
      if (built < 1970) return 'y1960'
      if (built < 1980) return 'y1970'
      if (built < 1990) return 'y1980'
      if (built < 2000) return 'y1990'
      if (built < 2010) return 'y2000'
      if (built < 2020) return 'y2010'
      if (built < 2030) return 'y2020' // NOVO
      return null
    },
    getTileIndex (x, y, z) {
      return defaultGetTileIndex(x, y, z)
    },
    getTilePosition (x, y, z) {
      return defaultGetTilePosition(x, y, z)
    }
  }
}
</script>

<style lang="scss">
.top-side-view {
  .vg-top-view-plot .vg-feature {
    stroke-width: 2;

    &.v-enter-active {
      transition: opacity 0.66667s;
    }

    &.v-enter {
      opacity: 0;
    }
  }

  .vg-side-view-plot .vg-feature {
    stroke-width: 0;
    mix-blend-mode: multiply;
  }

  .vg-feature {
    stroke: none;
    fill: none;

    &.y1960 {
      fill: #942170;
    }

    &.y1970 {
      fill: #c7246b;
    }

    &.y1980 {
      fill: #d74e68;
    }

    &.y1990 {
      fill: #ed6a59;
    }

    &.y2000 {
      fill: #ef8b4c;
    }

    &.y2010 {
      fill: #f4bb49;
    }

    &.y2020 {
      fill: #ffd400;
    }
  }

  .vg-label-bg {
    stroke: none;
    fill: none;
    width: 50px !important;
    height: 25px !important;


    &.y1960 {
      fill: #942170;
    }

    &.y1970 {
      fill: #c7246b;
    }

    &.y1980 {
      fill: #d74e68;
    }

    &.y1990 {
      fill: #ed6a59;
    }

    &.y2000 {
      fill: #ef8b4c;
    }

    &.y2010 {
      fill: #f4bb49;
    }

    &.y2020 {
      fill: #ffd400;
    }
  }

  .vg-text-label {
    fill: white;
    font-family: "CuratorRegular", "Helvetica Neue", Helvetica, Arial,
      sans-serif;
    font-size: 30px;
  }
}
</style>
